import { useEffect, useState } from 'react';
import { X, Save, Loader2, Link2, Unlink } from 'lucide-react';
import toast from 'react-hot-toast';
import { useVertical } from '../../../context/VerticalContext';
import { supabase } from '../../../lib/supabase';
import { formatJobNumber } from '../../../lib/docNumbers';
import { errorMessage } from '../../../lib/errors';
import type { JobLinkReview } from '../../../types/finance';
import { jobLinkMethodLabel, JOB_LINK_REVIEW_REASON } from './jobLink';

interface JobLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    headerId: string | null;
    headerVendor?: string | null;
    currentJobId?: string | null;
    linkMethod?: string | null;
    review?: JobLinkReview | null;
}

interface ActiveJobRow {
    id: string;
    job_number: number | null;
    title: string | null;
    customer_name: string | null;
    vehicle_reg: string | null;
    status: string | null;
}

export function JobLinkModal({
    isOpen,
    onClose,
    onSuccess,
    headerId,
    headerVendor,
    currentJobId,
    linkMethod,
    review,
}: JobLinkModalProps) {
    const candidates = review?.candidates ?? [];
    const { activeOrgId } = useVertical();
    const [activeJobs, setActiveJobs] = useState<ActiveJobRow[]>([]);
    const [selectedJobId, setSelectedJobId] = useState<string>('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        // An open question starts on the best suggestion; the owner confirms.
        setSelectedJobId(currentJobId || review?.candidates?.[0]?.job_id || '');
    }, [isOpen, currentJobId, review]);

    useEffect(() => {
        if (!isOpen || !activeOrgId) return;
        supabase
            .from('jobs')
            .select('id, job_number, title, status, customer:customers(full_name), vehicle:vehicles(vrm)')
            .eq('org_id', activeOrgId)
            .is('deleted_at', null)
            .not('status', 'in', '(cancelled,draft)')
            .order('created_at', { ascending: false })
            .limit(100)
            .then(({ data, error }) => {
                if (error) {
                    // Don't swallow — a schema drift here silently empties the picker.
                    console.error('JobLinkModal: failed to load active jobs', error);
                    toast.error('Could not load jobs');
                    setActiveJobs([]);
                    return;
                }
                setActiveJobs((data ?? []).map((j: any) => ({
                    id: j.id,
                    job_number: j.job_number ?? null,
                    title: j.title,
                    status: j.status,
                    customer_name: j.customer?.full_name?.trim() || null,
                    vehicle_reg: j.vehicle?.vrm ?? null,
                })));
            });
    }, [isOpen, activeOrgId]);

    const handleSave = async () => {
        if (!headerId) return;
        setSaving(true);
        try {
            // Clearing job_link_review answers the question even when the
            // answer is "no job" (job_id unchanged); tr_ledger_job_link_manual
            // marks the link manual and closes the notification.
            const { error } = await supabase
                .from('ledger_headers')
                .update({ job_id: selectedJobId || null, job_link_review: null })
                .eq('id', headerId);
            if (error) throw error;
            toast.success(selectedJobId ? 'Linked to job' : review ? 'Marked as not for a job' : 'Job link cleared');
            onSuccess?.();
            onClose();
        } catch (e) {
            console.error('Job link update failed:', e);
            toast.error(errorMessage(e) || 'Failed to update job link');
        } finally {
            setSaving(false);
        }
    };

    const handleUnlink = async () => {
        if (!headerId) return;
        setSelectedJobId('');
        // Save immediately so the user doesn't have to click twice.
        setSaving(true);
        try {
            const { error } = await supabase
                .from('ledger_headers')
                .update({ job_id: null, job_link_review: null })
                .eq('id', headerId);
            if (error) throw error;
            toast.success(currentJobId ? 'Job link cleared' : 'Marked as not for a job');
            onSuccess?.();
            onClose();
        } catch (e) {
            toast.error(errorMessage(e) || 'Failed to unlink');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-scrim/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-panel border border-line rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-line bg-raised">
                    <div>
                        <h2 className="text-lg font-bold text-fg flex items-center gap-2">
                            <Link2 className="w-5 h-5" />
                            Link expense to job
                        </h2>
                        {headerVendor && (
                            <p className="text-sm text-fg-3 mt-1 truncate max-w-xs">{headerVendor}</p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="p-2 hover:bg-raised rounded-full transition-colors text-fg-3 hover:text-fg shrink-0 disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {review && (
                        <div className="space-y-2">
                            <p className="text-sm text-fg-2">
                                {JOB_LINK_REVIEW_REASON[review.reason] ?? 'The upload could not tell which job this is for.'}
                            </p>
                            {candidates.length > 0 && (
                                <div className="space-y-2" role="radiogroup" aria-label="Suggested jobs">
                                    {candidates.map((c) => {
                                        const active = selectedJobId === c.job_id;
                                        return (
                                            <button
                                                key={c.job_id}
                                                type="button"
                                                role="radio"
                                                aria-checked={active}
                                                onClick={() => setSelectedJobId(c.job_id)}
                                                className={`w-full min-h-[44px] px-3 py-2 rounded-lg border text-left flex items-center justify-between gap-3 transition-colors ${active
                                                    ? 'border-gold/50 bg-gold-fill/10 text-fg'
                                                    : 'border-line bg-card text-fg-2 hover:bg-raised'}`}
                                            >
                                                <span className="min-w-0 truncate text-sm font-semibold">
                                                    {c.job_number != null ? formatJobNumber(c.job_number) : 'Job'}
                                                    {c.vrm ? ` · ${c.vrm}` : ''}
                                                </span>
                                                {c.lines > 0 && (
                                                    <span className="shrink-0 text-xs text-fg-3">
                                                        {c.matched}/{c.lines} lines match
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    {!review && jobLinkMethodLabel(linkMethod) && currentJobId && (
                        <p className="text-xs text-fg-3">{jobLinkMethodLabel(linkMethod)}</p>
                    )}
                    <label className="block">
                        <span className="text-xs font-medium text-fg-3 uppercase tracking-wider mb-1.5 block">
                            {review ? 'Or pick any job' : 'Active jobs'}
                        </span>
                        <select
                            value={selectedJobId}
                            onChange={(e) => setSelectedJobId(e.target.value)}
                            className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors"
                        >
                            <option value="">— No job link —</option>
                            {activeJobs.map((job) => {
                                const num = job.job_number != null ? `${formatJobNumber(job.job_number)} · ` : '';
                                const tag = job.vehicle_reg ? ` · ${job.vehicle_reg}` : '';
                                const customer = job.customer_name || 'Unnamed';
                                const titlePart = job.title ? ` · ${job.title}` : '';
                                const done = job.status === 'completed' ? ' [Done]' : '';
                                return (
                                    <option key={job.id} value={job.id}>
                                        {num}{customer}{tag}{titlePart}{done}
                                    </option>
                                );
                            })}
                        </select>
                    </label>
                    <p className="text-[11px] text-fg-4">
                        Linking reconciles this purchase against the job's Working Capital reservation.
                    </p>
                </div>

                <div className="p-6 border-t border-line bg-raised flex justify-between items-center">
                    {currentJobId || review ? (
                        <button
                            onClick={handleUnlink}
                            disabled={saving}
                            className="flex items-center gap-1.5 min-h-[44px] text-xs text-fg-3 hover:text-danger transition-colors disabled:opacity-50"
                        >
                            <Unlink className="w-3.5 h-3.5" />
                            {currentJobId ? 'Clear link' : 'Not for a job'}
                        </button>
                    ) : <div />}
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={saving}
                            className="px-4 py-2.5 bg-raised hover:bg-pressed disabled:opacity-50 text-fg rounded-lg text-sm font-medium transition-colors border border-line-strong"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="fd-btn-primary disabled:opacity-50 px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {saving ? 'Saving…' : 'Save link'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
